import { allows } from "@giftforward/entitlements";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SouthIcon from "@mui/icons-material/South";
import Tooltip from "@mui/material/Tooltip";
import Accordion from "@/components/campaignBuilder/Accordion";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useMyEntitlements } from "@/hooks/useMyEntitlements";
import { useOpenRecipientCapBlocked, useRecipientCap } from "@/hooks/useRecipientCap";
import { downloadRecipientTemplate } from "@/lib/recipientTemplate";
import { findStrandedRecipientTags } from "@/utils/strandedTags";
import LinkQuantityStepper from "./LinkQuantityStepper";
import SenderInfoFields from "./SenderInfoFields";
import { ConditionalPanel, RadioOption } from "./shared";
import UniversalLinkPanel from "./UniversalLinkPanel";

interface Props {
  isOpen: boolean;
  onToggle: () => void;
}

const LINK_LIST_TOOLTIP =
  "Optional. Linking a list ties each gift link to a person, so your redemption report shows who redeemed — and names can personalize the unboxing and landing page.";

export default function RecipientNotification({ isOpen, onToggle }: Props) {
  const {
    notificationMode,
    setNotificationMode,
    linkToList,
    setLinkToList,
    linkQuantity,
    setLinkQuantity,
    senderFirstName,
    setSenderFirstName,
    senderLastName,
    setSenderLastName,
    senderCompany,
    setSenderCompany,
    selectedBudget,
    stage3Landing,
    stage3Unboxing,
    openModal,
    openPlanModal,
    closeModal,
  } = useCampaignBuilder();

  const isManual = notificationMode === "manual";
  const isUniversal = notificationMode === "universal";

  // Manual + universal gift links are Pro+ features (entitlement redemption.links "manual"/"universal").
  // On a plan that lacks one the option shows a PRO badge and opens the upgrade modal instead of
  // selecting the mode.
  const { ent } = useMyEntitlements();
  const canManual = !!ent && allows(ent, "redemption", "links", "manual");
  const canUniversal = !!ent && allows(ent, "redemption", "links", "universal");

  // cap is null (unlimited) until the matrix resolves, so nothing is clamped on an unknown plan.
  const { cap } = useRecipientCap();
  const openCapBlocked = useOpenRecipientCapBlocked();

  function openManualUpsell() {
    openModal({
      context: "manualLinks",
      onPrimary: () => openPlanModal("manualLinks"),
    });
  }

  function openUniversalUpsell() {
    openModal({
      context: "universalLinks",
      onPrimary: () => openPlanModal("universalLinks"),
    });
  }

  // Warn when the landing copy still uses recipient-only tags (@FirstName/@LastName) but the links
  // are about to stop being tied to named people — so those tags would render blank. The tags are
  // NOT removed; the sender fixes their own copy. Informational ack only.
  function warnIfStrandedTags() {
    const stranded = findStrandedRecipientTags([
      stage3Landing.headline,
      stage3Landing.subtitle,
      stage3Unboxing.giftMessageText,
    ]);
    if (stranded.length > 0) {
      openModal({ context: "strandedTags", strandedTags: stranded, onPrimary: closeModal });
    }
  }

  // Switching gfg → manual: the links become unnamed until the sender answers "link a list?", so
  // recipient tags stop resolving. Fires only on the genuine gfg → manual edge.
  function selectManual() {
    // Paywall first — an unentitled plan can never reach the mode, so it can never save it either.
    if (!canManual) {
      openManualUpsell();
      return;
    }
    if (notificationMode === "gfg") warnIfStrandedTags();
    setNotificationMode("manual");
  }

  // Switching to universal: like manual Path A it has no recipient list, so recipient tags stop
  // resolving — warn on the gfg → universal edge. linkToList is forced false (universal is always the
  // no-list, anonymous-by-quantity shape).
  function selectUniversal() {
    if (!canUniversal) {
      openUniversalUpsell();
      return;
    }
    if (notificationMode === "gfg") warnIfStrandedTags();
    setNotificationMode("universal");
    setLinkToList(false);
  }

  // "Link these gift links to a list of recipients?" — the manual distribution path.
  //   true  → Path B: one link per uploaded recipient; names resolve again (the list below appears).
  //   false → Path A: anonymous links by quantity; recipient tags won't resolve, so warn if the
  //           sender is leaving the named-list path (Path B → Path A) with tags still in their copy.
  function chooseLinkToList(linked: boolean) {
    if (!linked && linkToList === true) warnIfStrandedTags();
    setLinkToList(linked);
  }

  const description = isUniversal
    ? "Generate a single universal link"
    : isManual
      ? "You'll send gift links manually"
      : "We notify your recipients via email";

  return (
    <Accordion
      title="Recipient Notification"
      description={description}
      icon={<ForwardToInboxIcon sx={{ fontSize: 24 }} />}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-2 mt-4">
        <RadioOption
          selected={notificationMode === "gfg"}
          onSelect={() => setNotificationMode("gfg")}
          label="We notify your recipients via email"
          hint="We handle the delivery — beautifully branded emails sent on your behalf with tracking and reminders built in."
          recommended
        />
        {/* Universal link — 2nd, right after GfG email and before manual (matches the prototype). */}
        <RadioOption
          selected={isUniversal}
          onSelect={selectUniversal}
          label="Generate a single universal link"
          hint="Display the link wherever you want — multiple people can click it and redeem a gift."
          hasVisiblePanel={isUniversal}
          pro={!canUniversal}
        />
        {/* Universal mode: the multiple-use terms gate, then (once accepted) the public link + the
            redemption-limit stepper + campaign-level sender. */}
        <ConditionalPanel visible={isUniversal} merged>
          <UniversalLinkPanel />
        </ConditionalPanel>
        <RadioOption
          selected={isManual}
          onSelect={selectManual}
          label="You'll send gift links manually"
          hint="You'll get a CSV of unique gift links to share through your own channels."
          hasVisiblePanel={isManual}
          pro={!canManual}
        />

        {/* Manual mode: first choose how the links are distributed, then show only what that path
            needs. The question stays visible while unanswered (linkToList === null) — Step 2's
            Continue waits on it. */}
        <ConditionalPanel visible={isManual} merged>
          <p className="font-lato text-[14px] font-semibold text-rodeo-black mb-2 flex items-center gap-1.5">
            Link these gift links to a list of recipients?
            <Tooltip title={LINK_LIST_TOOLTIP} enterTouchDelay={0} arrow>
              <InfoOutlinedIcon
                className="shrink-0 cursor-help text-graphite"
                sx={{ fontSize: 16 }}
                aria-label="Why link a recipient list"
                role="img"
                tabIndex={0}
              />
            </Tooltip>
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <RadioOption
                selected={linkToList === false}
                onSelect={() => chooseLinkToList(false)}
                label="No, just set a quantity"
                hint="Links aren't tied to anyone until you hand them out."
              />
            </div>
            <div className="flex-1">
              <RadioOption
                selected={linkToList === true}
                onSelect={() => chooseLinkToList(true)}
                label="Yes, link a recipient list"
                hint="One link per recipient, so you can see who redeemed."
              />
            </div>
          </div>

          {/* Path A — anonymous links by quantity. No recipient rows, so this count is the budget
              denominator (Step 4 prices off it) and these sender fields are the source of the
              unboxing page's @SenderFirstName / @SenderCompany tokens. */}
          {linkToList === false && (
            <div className="mt-5">
              <LinkQuantityStepper
                label="How many gift links do you need?"
                value={linkQuantity}
                onChange={setLinkQuantity}
                tier={selectedBudget ?? 0}
                cap={cap}
                onOverCap={openCapBlocked}
              />
              <SenderInfoFields
                className="mt-5"
                firstName={senderFirstName}
                onFirstName={setSenderFirstName}
                lastName={senderLastName}
                onLastName={setSenderLastName}
                company={senderCompany}
                onCompany={setSenderCompany}
                helper="Recipients don't get an email introducing you, so this is what the unboxing page uses to say who the gift is from."
              />
            </div>
          )}

          {/* Path B — one link per recipient. The list itself is the Add Recipients section below;
              the per-row sender + Signature columns replace the campaign-level fields above. */}
          {linkToList === true && (
            <div className="mt-5 flex items-start gap-2 rounded-btn border border-smoke bg-white px-3 py-2.5">
              <SouthIcon sx={{ fontSize: 18 }} className="text-dark-dust shrink-0 mt-px" />
              <p className="font-lato text-[13px] text-graphite leading-relaxed m-0">
                Add your recipients below — you'll get one gift link for each.{" "}
                <button
                  type="button"
                  onClick={() => downloadRecipientTemplate(ent, { withSendDate: false })}
                  className="font-lato text-[13px] font-semibold text-dark-dust underline underline-offset-2 hover:text-rodeo-black"
                >
                  Download recipients template
                </button>
              </p>
            </div>
          )}
        </ConditionalPanel>
      </div>
    </Accordion>
  );
}
